import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, mergeProps as _mergeProps } from "vue";
const _hoisted_1 = {
    key: 0,
    class: "map-overlay text-center text-sm text-neutral-600 dark:text-neutral-300"
};
const _hoisted_2 = {
    key: 1,
    class: "map-overlay"
};
const _hoisted_3 = ["title", "src"];
const _hoisted_4 = {
    key: 4,
    class: "map-overlay text-center text-sm text-neutral-600 dark:text-neutral-300"
};
const _hoisted_5 = {
    key: 0,
    class: "mt-2"
};
const _hoisted_6 = ["href"];
const _hoisted_7 = {
    key: 0,
    class: "place-tag"
};
const _hoisted_8 = {
    key: 0,
    class: "place-tag__image"
};
const _hoisted_9 = ["src", "alt"];
const _hoisted_10 = { class: "place-tag__content" };
const _hoisted_11 = {
    key: 0,
    class: "place-tag__address"
};
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
const MAX_PLACE_PHOTOS = 1;
export default /*@__PURE__*/ _defineComponent({
    ...{ name: 'ProjectLocationMap', inheritAttrs: true },
    __name: 'ProjectLocationMap',
    props: {
        placeId: {},
        coordinates: {},
        scriptUrl: {},
        statusEndpoint: { default: '/public/google-maps-status' },
        placeDetailsEndpoint: { default: '/public/places/details' },
        heightClass: { default: 'h-64' },
        zoom: { default: 16 },
        mapId: {},
        mapOptions: {},
        useIframeFallback: { type: Boolean, default: true },
        iframeTitle: { default: 'Google Maps' },
        statusWithCredentials: { type: Boolean, default: false }
    },
    emits: ["place-details", "map-ready", "map-error"],
    setup(__props, { expose: __expose, emit: __emit }) {
        const DEFAULT_CENTER = { lat: 51.1657, lng: 10.4515 };
        const props = __props;
        const emit = __emit;
        const normalizedPlaceId = computed(() => props.placeId?.trim() ?? '');
        const mapContainer = ref(null);
        const mapInstance = ref(null);
        const markerInstance = ref(null);
        const mapStatus = reactive({ checking: false, configured: false, scriptUrl: '', mapId: null });
        const mapError = ref(null);
        const isFetchingPlace = ref(false);
        const resolvedCoordinates = ref(props.coordinates ? { ...props.coordinates } : null);
        const placeMetadata = ref(null);
        const mapReadyState = ref(false);
        const resolvedMapId = computed(() => props.mapId ?? mapStatus.mapId ?? undefined);
        const placePhotos = ref([]);
        const photosLoadedForPlace = ref(null);
        const scriptPromises = new Map();
        let abortController = null;
        let mapInitPromise = null;
        let mapsLibraryPromise = null;
        let placesLibraryPromise = null;
        let MapConstructor = null;
        let placePhotosPromise = null;
        let placesLibraryModule = null;
        const isLabelVisible = ref(true);
        const primaryPlacePhoto = computed(() => placePhotos.value[0] ?? null);
        const placeTagVisible = computed(() => isLabelVisible.value && (placeLabelText.value || Boolean(primaryPlacePhoto.value)));
        const closePlaceTag = () => {
            isLabelVisible.value = false;
        };
        const iframeUrl = computed(() => normalizedPlaceId.value ? `https://www.google.com/maps?q=place_id:${normalizedPlaceId.value}&output=embed` : '');
        const externalMapUrl = computed(() => {
            if (!normalizedPlaceId.value) {
                return '';
            }
            const queryValue = encodeURIComponent(`place_id:${normalizedPlaceId.value}`);
            return `https://www.google.com/maps/search/?api=1&query=${queryValue}`;
        });
        const useIframeFallback = computed(() => props.useIframeFallback);
        const placeLabelText = computed(() => placeMetadata.value?.name || placeMetadata.value?.formattedAddress || '');
        const STATIC_INTERACTION_OPTIONS = {
            disableDefaultUI: true,
            zoomControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            mapTypeControl: false,
            scaleControl: false,
            rotateControl: false,
            gestureHandling: 'none',
            draggable: false,
            scrollwheel: false,
            disableDoubleClickZoom: true,
            keyboardShortcuts: false,
            clickableIcons: false
        };
        const getPreferredLanguage = () => {
            const lang = document.documentElement?.lang || 'de';
            return lang.split('-')[0]?.toLowerCase() || 'de';
        };
        const buildAuthorAttributionLink = (author) => {
            if (!author) {
                return null;
            }
            const label = escapeHtml(author.displayName || 'Google Maps');
            if (author.uri) {
                const safeHref = escapeHtml(author.uri);
                return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer">${label}</a>`;
            }
            return label;
        };
        const resolvePlacePhotoUrl = (photo) => {
            if (!photo) {
                return '';
            }
            if (typeof photo.createUrl === 'function') {
                return photo.createUrl({ maxHeight: 640, maxWidth: 960 });
            }
            if (typeof photo.getURI === 'function') {
                return photo.getURI({ maxHeight: 640, maxWidth: 960 });
            }
            if (typeof photo.getUrl === 'function') {
                return photo.getUrl({ maxHeight: 640, maxWidth: 960 });
            }
            return photo.photoUrl || photo.url || '';
        };
        const getPlaceConstructor = () => {
            const placesNamespace = window.google?.maps?.places || null;
            return placesLibraryModule?.Place || placesNamespace?.Place || null;
        };
        const hasPlacesApiAvailable = () => {
            const namespace = window.google?.maps?.places || null;
            return Boolean(namespace && (namespace.Place || namespace.PlacesService));
        };
        const escapeHtml = (value) => value.replace(/[&<>"']/g, (char) => {
            switch (char) {
                case '&':
                    return '&amp;';
                case '<':
                    return '&lt;';
                case '>':
                    return '&gt;';
                case '"':
                    return '&quot;';
                case "'":
                    return '&#39;';
                default:
                    return char;
            }
        });
        const buildAttributionHtml = (rawAttribution) => {
            if (!rawAttribution) {
                return null;
            }
            const hrefMatch = rawAttribution.match(/href="([^"]+)"/i);
            const href = hrefMatch?.[1]?.trim();
            const labelText = rawAttribution.replace(/<[^>]+>/g, '').trim();
            const safeLabel = escapeHtml(labelText || 'Google Maps');
            if (!href) {
                return safeLabel;
            }
            const safeHref = escapeHtml(href);
            return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer">${safeLabel}</a>`;
        };
        const hasResolvedCoordinates = computed(() => typeof resolvedCoordinates.value?.lat === 'number' && typeof resolvedCoordinates.value?.lng === 'number');
        const ensureCoordinates = async () => {
            if (!normalizedPlaceId.value || props.coordinates?.lat || props.coordinates?.lng) {
                resolvedCoordinates.value = props.coordinates ? { ...props.coordinates } : resolvedCoordinates.value;
                return;
            }
            if (!props.placeDetailsEndpoint) {
                resolvedCoordinates.value = null;
                return;
            }
            isFetchingPlace.value = true;
            mapError.value = null;
            try {
                abortController?.abort();
                abortController = new AbortController();
                const response = await fetch(props.placeDetailsEndpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ placeId: normalizedPlaceId.value }),
                    signal: abortController.signal
                });
                if (!response.ok) {
                    throw new Error(`Place-Details Anfrage fehlgeschlagen (${response.status})`);
                }
                const payload = await response.json();
                const place = payload?.data;
                const location = place?.geometry?.location;
                if (location?.lat && location?.lng) {
                    resolvedCoordinates.value = { lat: location.lat, lng: location.lng };
                    placeMetadata.value = {
                        name: place?.name,
                        formattedAddress: place?.formatted_address
                    };
                    // Emit place details immediately after resolving
                    emit('place-details', { ...placeMetadata.value });
                    console.log('[ProjectLocationMap] Place details emitted:', placeMetadata.value);
                }
                else {
                    resolvedCoordinates.value = null;
                    emit('place-details', null);
                    console.warn('[ProjectLocationMap] Place Details lieferten keine Koordinaten.', place);
                }
            }
            catch (error) {
                if (error.name === 'AbortError') {
                    return;
                }
                console.error('[ProjectLocationMap] Fehler beim Laden der Place Details', error);
                resolvedCoordinates.value = null;
                mapError.value = 'Koordinaten konnten nicht ermittelt werden.';
            }
            finally {
                isFetchingPlace.value = false;
            }
        };
        const applyScriptConfig = (config) => {
            const scriptUrl = config?.scriptUrl;
            mapStatus.configured = Boolean(scriptUrl);
            mapStatus.scriptUrl = scriptUrl || '';
            mapStatus.mapId = config?.mapId ?? null;
            console.log('[ProjectLocationMap] applyScriptConfig:', {
                configured: mapStatus.configured,
                scriptUrl: mapStatus.scriptUrl,
                mapId: mapStatus.mapId
            });
        };
        const fetchMapStatus = async () => {
            console.log('[ProjectLocationMap] fetchMapStatus started, props.scriptUrl:', props.scriptUrl, 'props.statusEndpoint:', props.statusEndpoint);
            if (props.scriptUrl) {
                applyScriptConfig({ scriptUrl: props.scriptUrl, mapId: props.mapId ?? null });
                return;
            }
            if (!props.statusEndpoint) {
                console.log('[ProjectLocationMap] No statusEndpoint, skipping fetch');
                applyScriptConfig();
                return;
            }
            mapStatus.checking = true;
            mapError.value = null;
            try {
                const fallbackEndpoints = [];
                if (props.statusWithCredentials &&
                    props.statusEndpoint === '/public/google-maps-status' &&
                    !fallbackEndpoints.includes('/api/settings/google-maps-status')) {
                    // Backward compatibility for environments where only the admin status route exists.
                    fallbackEndpoints.push('/api/settings/google-maps-status');
                }
                const candidateEndpoints = [props.statusEndpoint, ...fallbackEndpoints];
                let lastError = null;
                for (const endpoint of candidateEndpoints) {
                    try {
                        console.log('[ProjectLocationMap] Fetching map status from:', endpoint);
                        const response = await fetch(endpoint, {
                            credentials: props.statusWithCredentials ? 'include' : 'same-origin'
                        });
                        const rawBody = await response.text();
                        const contentType = response.headers.get('content-type') || '';
                        if (!response.ok) {
                            throw new Error(`Status Anfrage fehlgeschlagen (${response.status})`);
                        }
                        if (!contentType.includes('application/json')) {
                            throw new Error(`Status Endpoint lieferte kein JSON (content-type: ${contentType || 'unknown'})`);
                        }
                        const data = JSON.parse(rawBody);
                        console.log('[ProjectLocationMap] Map status response:', data);
                        applyScriptConfig({ scriptUrl: data?.scriptUrl, mapId: data?.mapId ?? null });
                        mapStatus.message = data?.message;
                        if (!mapStatus.scriptUrl) {
                            mapError.value = 'Keine Google Maps Konfiguration gefunden.';
                            console.warn('[ProjectLocationMap] No scriptUrl in response');
                        }
                        return;
                    }
                    catch (endpointError) {
                        lastError = endpointError;
                        console.warn('[ProjectLocationMap] Map status endpoint failed:', endpoint, endpointError);
                    }
                }
                throw lastError || new Error('Google Maps Status Endpoint nicht erreichbar');
            }
            catch (error) {
                console.error('[ProjectLocationMap] Fehler beim Prüfen des Google Maps Status', error);
                mapError.value = 'Google Maps API Key nicht verfügbar.';
                applyScriptConfig();
            }
            finally {
                mapStatus.checking = false;
            }
        };
        const loadGoogleMapsScript = async (scriptUrl) => {
            if (!scriptUrl) {
                throw new Error('Kein Google Maps Script-URL definiert');
            }
            if (window.google?.maps) {
                return;
            }
            if (!scriptPromises.has(scriptUrl)) {
                scriptPromises.set(scriptUrl, new Promise((resolve, reject) => {
                    const script = document.createElement('script');
                    script.src = scriptUrl;
                    script.async = true;
                    script.defer = true;
                    script.onload = () => resolve();
                    script.onerror = () => reject(new Error('Google Maps Script konnte nicht geladen werden.'));
                    document.head.appendChild(script);
                }));
            }
            await scriptPromises.get(scriptUrl);
        };
        const waitForGoogle = async () => {
            const maxAttempts = 30;
            let attempts = 0;
            while ((!window.google || !window.google.maps) && attempts < maxAttempts) {
                await new Promise((resolve) => setTimeout(resolve, 100));
                attempts += 1;
            }
            if (!window.google?.maps) {
                throw new Error('Google Maps API nicht verfügbar');
            }
        };
        const waitForMapConstructor = async () => {
            const maxAttempts = 40;
            let attempts = 0;
            while (!window.google?.maps?.Map && attempts < maxAttempts) {
                await new Promise((resolve) => setTimeout(resolve, 150));
                attempts += 1;
            }
            return window.google?.maps?.Map ?? null;
        };
        const ensureMapsLibrary = async () => {
            console.log('[ProjectLocationMap] ensureMapsLibrary started, window.google?.maps:', !!window.google?.maps, 'MapConstructor:', !!MapConstructor);
            console.log('[ProjectLocationMap] window.google?.maps?.Map:', !!window.google?.maps?.Map);
            console.log('[ProjectLocationMap] window.google?.maps keys:', window.google?.maps ? Object.keys(window.google.maps) : 'N/A');
            console.log('[ProjectLocationMap] window.google?.maps?.importLibrary:', !!window.google?.maps?.importLibrary);
            if (!window.google?.maps) {
                mapError.value = 'Google Maps API nicht verfügbar';
                console.error('[ProjectLocationMap] window.google.maps not available');
                emit('map-error', mapError.value);
                return false;
            }
            if (MapConstructor) {
                console.log('[ProjectLocationMap] MapConstructor already set');
                return true;
            }
            const hadImportLibrary = typeof window.google.maps.importLibrary === 'function';
            if (hadImportLibrary) {
                console.log('[ProjectLocationMap] importLibrary available - loading maps library...');
                if (!mapsLibraryPromise) {
                    mapsLibraryPromise = window.google.maps
                        .importLibrary('maps')
                        .then((library) => {
                        console.log('[ProjectLocationMap] importLibrary resolved, library:', library);
                        MapConstructor = library?.Map ?? null;
                        if (!MapConstructor && window.google?.maps?.Map) {
                            MapConstructor = window.google.maps.Map;
                            console.log('[ProjectLocationMap] MapConstructor recovered from global after import');
                        }
                    })
                        .catch((error) => {
                        console.error('[ProjectLocationMap] importLibrary failed:', error);
                        mapsLibraryPromise = null;
                        throw error;
                    });
                }
                try {
                    await mapsLibraryPromise;
                }
                catch (error) {
                    mapError.value = 'Google Maps Bibliothek konnte nicht importiert werden.';
                    emit('map-error', mapError.value);
                    return false;
                }
            }
            if (!hadImportLibrary) {
                console.log('[ProjectLocationMap] importLibrary not available, waiting for global Map constructor');
                const globalMap = await waitForMapConstructor();
                if (globalMap) {
                    MapConstructor = globalMap;
                    console.log('[ProjectLocationMap] MapConstructor resolved via waitForMapConstructor');
                }
            }
            if (!MapConstructor && window.google?.maps?.Map) {
                MapConstructor = window.google.maps.Map;
                console.log('[ProjectLocationMap] MapConstructor assigned from global fallback');
            }
            if (!MapConstructor) {
                mapError.value = 'Google Maps Map Konstruktor nicht verfügbar';
                console.error('[ProjectLocationMap] MapConstructor still null after all attempts');
                emit('map-error', mapError.value);
                return false;
            }
            console.log('[ProjectLocationMap] ensureMapsLibrary succeeded, MapConstructor:', !!MapConstructor);
            return true;
        };
        const waitForPlacesNamespace = async () => {
            const maxAttempts = 40;
            let attempts = 0;
            while (!hasPlacesApiAvailable() && attempts < maxAttempts) {
                await new Promise((resolve) => setTimeout(resolve, 150));
                attempts += 1;
            }
            return hasPlacesApiAvailable();
        };
        const ensurePlacesLibrary = async () => {
            if (!window.google?.maps) {
                return false;
            }
            if (typeof window.google.maps.importLibrary === 'function') {
                if (!placesLibraryPromise) {
                    placesLibraryPromise = window.google.maps
                        .importLibrary('places')
                        .then((library) => {
                        placesLibraryModule = library;
                        if (!hasPlacesApiAvailable() && !placesLibraryModule?.Place) {
                            throw new Error('Places API nicht verfügbar');
                        }
                    })
                        .catch((error) => {
                        console.error('[ProjectLocationMap] importLibrary("places") failed:', error);
                        placesLibraryPromise = null;
                        placesLibraryModule = null;
                        throw error;
                    });
                }
                try {
                    await placesLibraryPromise;
                    return hasPlacesApiAvailable() || Boolean(placesLibraryModule?.Place);
                }
                catch (error) {
                    return false;
                }
            }
            const hasPlaces = await waitForPlacesNamespace();
            if (!hasPlaces) {
                console.warn('[ProjectLocationMap] Places namespace nicht verfügbar');
            }
            return hasPlaces;
        };
        const initMap = () => {
            console.log('[ProjectLocationMap] initMap called, mapContainer:', !!mapContainer.value, 'mapInstance:', !!mapInstance.value);
            if (!mapContainer.value || mapInstance.value) {
                console.log('[ProjectLocationMap] initMap skipped (no container or map already exists)');
                return;
            }
            const mapCtor = MapConstructor || window.google?.maps?.Map;
            console.log('[ProjectLocationMap] initMap mapCtor:', !!mapCtor);
            if (!mapCtor) {
                const message = 'Google Maps Map Konstruktor nicht verfügbar';
                mapError.value = message;
                console.error('[ProjectLocationMap]', message);
                emit('map-error', message);
                return;
            }
            const center = hasResolvedCoordinates.value
                ? { lat: resolvedCoordinates.value.lat, lng: resolvedCoordinates.value.lng }
                : DEFAULT_CENTER;
            console.log('[ProjectLocationMap] Creating map with center:', center, 'zoom:', hasResolvedCoordinates.value ? props.zoom : 5);
            const mapOptions = {
                center,
                zoom: hasResolvedCoordinates.value ? props.zoom : 5,
                mapId: resolvedMapId.value,
                mapTypeId: 'hybrid',
                ...props.mapOptions,
                ...STATIC_INTERACTION_OPTIONS
            };
            mapInstance.value = new mapCtor(mapContainer.value, mapOptions);
            applyInteractionRestrictions();
            mapReadyState.value = true;
            console.log('[ProjectLocationMap] Map created successfully, emitting map-ready');
            emit('map-ready');
            // Ensure marker is created after map is ready
            if (hasResolvedCoordinates.value) {
                console.log('[ProjectLocationMap] Coordinates available, creating marker immediately');
                updateMarker();
            }
            else {
                console.log('[ProjectLocationMap] No coordinates yet, marker will be created when coordinates resolve');
            }
        };
        const clearMarker = () => {
            if (!markerInstance.value) {
                return;
            }
            if ('setMap' in markerInstance.value && typeof markerInstance.value.setMap === 'function') {
                markerInstance.value.setMap(null);
            }
            else if ('map' in markerInstance.value) {
                markerInstance.value.map = null;
            }
            markerInstance.value = null;
        };
        const applyInteractionRestrictions = () => {
            if (!mapInstance.value) {
                return;
            }
            mapInstance.value.setOptions(STATIC_INTERACTION_OPTIONS);
        };
        const createCustomMarkerElement = () => {
            const element = document.createElement('div');
            element.className = 'project-location-advanced-marker';
            return element;
        };
        const createMarkerForPosition = (position) => {
            console.log('[ProjectLocationMap] createMarkerForPosition called', {
                hasMap: !!mapInstance.value,
                hasGoogle: !!window.google?.maps,
                hasAdvancedMarker: !!window.google?.maps?.marker?.AdvancedMarkerElement,
                hasPinElement: !!window.google?.maps?.marker?.PinElement,
                hasMapId: !!resolvedMapId.value
            });
            if (!mapInstance.value || !window.google?.maps) {
                console.error('[ProjectLocationMap] Cannot create marker - map or google not available');
                return null;
            }
            const title = placeLabelText.value || 'Restaurant Location';
            // Try PinElement with AdvancedMarkerElement first (most visible)
            if (window.google.maps.marker?.AdvancedMarkerElement && window.google.maps.marker?.PinElement) {
                console.log('[ProjectLocationMap] Creating PinElement marker');
                try {
                    const pinElement = new window.google.maps.marker.PinElement({
                        background: '#dc2626',
                        borderColor: '#ffffff',
                        glyphColor: '#ffffff',
                        glyph: '📍',
                        scale: 1.5
                    });
                    const marker = new window.google.maps.marker.AdvancedMarkerElement({
                        map: mapInstance.value,
                        position,
                        title,
                        content: pinElement.element,
                        zIndex: 999999,
                        gmpDraggable: false
                    });
                    console.log('[ProjectLocationMap] ✅ PinElement marker created successfully');
                    return marker;
                }
                catch (error) {
                    console.error('[ProjectLocationMap] PinElement marker creation failed:', error);
                }
            }
            // Try custom DOM element with AdvancedMarkerElement
            if (window.google.maps.marker?.AdvancedMarkerElement) {
                console.log('[ProjectLocationMap] Creating custom DOM marker');
                try {
                    const markerElement = createCustomMarkerElement();
                    const marker = new window.google.maps.marker.AdvancedMarkerElement({
                        map: mapInstance.value,
                        position,
                        title,
                        content: markerElement,
                        zIndex: 999999,
                        gmpDraggable: false
                    });
                    console.log('[ProjectLocationMap] ✅ Custom DOM marker created successfully');
                    return marker;
                }
                catch (error) {
                    console.error('[ProjectLocationMap] Custom DOM marker creation failed:', error);
                }
            }
            // Fallback to classic Marker with custom icon
            console.log('[ProjectLocationMap] Creating fallback classic Marker');
            try {
                const marker = new window.google.maps.Marker({
                    map: mapInstance.value,
                    position,
                    title,
                    animation: window.google.maps.Animation.DROP,
                    zIndex: 999999,
                    optimized: false,
                    icon: {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        fillColor: '#dc2626',
                        fillOpacity: 1,
                        strokeColor: '#ffffff',
                        strokeWeight: 3,
                        scale: 10
                    }
                });
                console.log('[ProjectLocationMap] ✅ Classic marker created successfully');
                return marker;
            }
            catch (error) {
                console.error('[ProjectLocationMap] Classic marker creation failed:', error);
                return null;
            }
        };
        const applyMarkerTitle = (title) => {
            if (!markerInstance.value) {
                return;
            }
            if ('setTitle' in markerInstance.value && typeof markerInstance.value.setTitle === 'function') {
                markerInstance.value.setTitle(title);
                return;
            }
            if ('title' in markerInstance.value) {
                ;
                markerInstance.value.title = title;
            }
        };
        const updateMarker = () => {
            console.log('[ProjectLocationMap] updateMarker called', {
                hasMap: !!mapInstance.value,
                hasCoords: hasResolvedCoordinates.value,
                coords: resolvedCoordinates.value,
                hasGoogle: !!window.google?.maps
            });
            if (!mapInstance.value || !hasResolvedCoordinates.value || !window.google?.maps) {
                console.log('[ProjectLocationMap] updateMarker skipped - missing requirements');
                return;
            }
            const position = new window.google.maps.LatLng(resolvedCoordinates.value.lat, resolvedCoordinates.value.lng);
            console.log('[ProjectLocationMap] Creating marker at position:', position.toString());
            clearMarker();
            markerInstance.value = createMarkerForPosition(position);
            console.log('[ProjectLocationMap] Marker created:', {
                exists: !!markerInstance.value,
                type: markerInstance.value ? (markerInstance.value.constructor.name || 'Unknown') : 'None'
            });
            if (!markerInstance.value) {
                console.warn('[ProjectLocationMap] Marker creation failed!');
                return;
            }
            mapInstance.value.setCenter(position);
            mapInstance.value.setZoom(props.zoom || 18);
            mapInstance.value.setMapTypeId('hybrid');
            applyMarkerTitle(placeLabelText.value || 'Restaurant Location');
            console.log('[ProjectLocationMap] Marker updated successfully');
            // Force marker visibility after a short delay
            setTimeout(() => {
                if (markerInstance.value && mapInstance.value) {
                    console.log('[ProjectLocationMap] Verifying marker after delay:', {
                        markerExists: !!markerInstance.value,
                        mapExists: !!mapInstance.value
                    });
                }
            }, 500);
        };
        const loadPhotosWithModernPlaceApi = async () => {
            const PlaceCtor = getPlaceConstructor();
            if (!PlaceCtor) {
                return false;
            }
            try {
                const place = new PlaceCtor({
                    id: normalizedPlaceId.value,
                    requestedLanguage: getPreferredLanguage(),
                    requestedRegion: 'DE'
                });
                if (typeof place.fetchFields !== 'function') {
                    return false;
                }
                await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'photos'] });
                const displayName = place.displayName?.text || place.displayName?.toString?.();
                const formattedAddress = place.formattedAddress || placeMetadata.value?.formattedAddress;
                if (displayName || formattedAddress) {
                    placeMetadata.value = {
                        name: displayName || placeMetadata.value?.name,
                        formattedAddress: formattedAddress || placeMetadata.value?.formattedAddress
                    };
                    emit('place-details', { ...placeMetadata.value });
                }
                if (Array.isArray(place.photos) && place.photos.length) {
                    placePhotos.value = place.photos.slice(0, MAX_PLACE_PHOTOS).map((photo) => ({
                        url: resolvePlacePhotoUrl(photo),
                        width: photo.widthPx ?? photo.width ?? 960,
                        height: photo.heightPx ?? photo.height ?? 640,
                        attributionHtml: buildAuthorAttributionLink(photo.authorAttributions?.[0] ?? null)
                    }));
                }
                else {
                    placePhotos.value = [];
                }
                return true;
            }
            catch (error) {
                console.warn('[ProjectLocationMap] Modern Places API failed, will try legacy service', error);
                return false;
            }
        };
        const loadPhotosWithLegacyService = async () => {
            if (!window.google?.maps?.places?.PlacesService) {
                return false;
            }
            const serviceContext = (mapInstance.value?.getDiv() || mapContainer.value || document.createElement('div'));
            const service = new window.google.maps.places.PlacesService(serviceContext);
            await new Promise((resolve) => {
                service.getDetails({
                    placeId: normalizedPlaceId.value,
                    fields: ['photos', 'name', 'formatted_address']
                }, (place, status) => {
                    const statusOk = status === window.google?.maps?.places?.PlacesServiceStatus?.OK;
                    if (statusOk && place) {
                        if (place.name || place.formatted_address) {
                            placeMetadata.value = {
                                name: place.name || placeMetadata.value?.name,
                                formattedAddress: place.formatted_address || placeMetadata.value?.formattedAddress
                            };
                            emit('place-details', { ...placeMetadata.value });
                        }
                        if (Array.isArray(place.photos) && place.photos.length) {
                            placePhotos.value = place.photos.slice(0, MAX_PLACE_PHOTOS).map((photo) => ({
                                url: photo.getUrl({ maxWidth: 960, maxHeight: 640 }),
                                width: photo.width ?? 960,
                                height: photo.height ?? 640,
                                attributionHtml: buildAttributionHtml(photo.html_attributions?.[0] ?? null)
                            }));
                        }
                        else {
                            placePhotos.value = [];
                        }
                    }
                    else {
                        console.warn('[ProjectLocationMap] PlacesService.getDetails returned no photos', { status, place });
                        placePhotos.value = [];
                    }
                    resolve();
                });
            });
            return true;
        };
        const loadPlacePhotos = async () => {
            if (!normalizedPlaceId.value || photosLoadedForPlace.value === normalizedPlaceId.value) {
                return;
            }
            if (placePhotosPromise) {
                await placePhotosPromise;
                return;
            }
            const inFlightPromise = (async () => {
                const hasPlacesLibrary = await ensurePlacesLibrary();
                if (!hasPlacesLibrary) {
                    return;
                }
                const usedModernApi = await loadPhotosWithModernPlaceApi();
                if (!usedModernApi) {
                    await loadPhotosWithLegacyService();
                }
                photosLoadedForPlace.value = normalizedPlaceId.value;
            })().catch((error) => {
                console.error('[ProjectLocationMap] Failed to load place photos', error);
                placePhotos.value = [];
                photosLoadedForPlace.value = normalizedPlaceId.value;
            });
            placePhotosPromise = inFlightPromise;
            try {
                await inFlightPromise;
            }
            finally {
                if (placePhotosPromise === inFlightPromise) {
                    placePhotosPromise = null;
                }
            }
        };
        const ensureMapReady = async () => {
            console.log('[ProjectLocationMap] ensureMapReady called, configured:', mapStatus.configured, 'hasContainer:', !!mapContainer.value, 'hasInstance:', !!mapInstance.value);
            if (!mapStatus.configured || !mapContainer.value) {
                console.log('[ProjectLocationMap] ensureMapReady skipped (not configured or no container)');
                return;
            }
            if (mapInstance.value) {
                console.log('[ProjectLocationMap] ensureMapReady skipped (map already exists)');
                return;
            }
            if (!mapInitPromise) {
                console.log('[ProjectLocationMap] Starting map initialization sequence');
                mapReadyState.value = false;
                mapInitPromise = (async () => {
                    console.log('[ProjectLocationMap] Step 1: Loading Google Maps script');
                    await loadGoogleMapsScript(mapStatus.scriptUrl);
                    console.log('[ProjectLocationMap] Step 2: Waiting for Google object');
                    await waitForGoogle();
                    console.log('[ProjectLocationMap] Step 3: Ensuring Maps library');
                    const libraryReady = await ensureMapsLibrary();
                    if (!libraryReady) {
                        console.error('[ProjectLocationMap] Library not ready, aborting');
                        mapInitPromise = null;
                        return;
                    }
                    console.log('[ProjectLocationMap] Step 4: Initializing map');
                    initMap();
                })().catch((error) => {
                    console.error('[ProjectLocationMap] Initialisierung fehlgeschlagen', error);
                    mapError.value = error.message;
                    mapReadyState.value = false;
                    emit('map-error', mapError.value || 'Google Maps initialisierung fehlgeschlagen');
                    mapInitPromise = null;
                });
            }
            else {
                console.log('[ProjectLocationMap] Map init already in progress, awaiting...');
            }
            await mapInitPromise;
            console.log('[ProjectLocationMap] ensureMapReady complete');
        };
        watch(() => props.coordinates, (coords) => {
            resolvedCoordinates.value = coords ? { ...coords } : null;
            updateMarker();
        }, { deep: true });
        watch(normalizedPlaceId, () => {
            placeMetadata.value = null;
            placePhotos.value = [];
            photosLoadedForPlace.value = null;
            placePhotosPromise = null;
            emit('place-details', null);
            ensureCoordinates();
            isLabelVisible.value = true;
            if (mapReadyState.value) {
                void loadPlacePhotos();
            }
        }, { immediate: false });
        watch(() => resolvedCoordinates.value, () => {
            updateMarker();
        }, { deep: true });
        watch(() => mapStatus.configured, (configured) => {
            if (configured) {
                ensureMapReady();
            }
        });
        watch(mapReadyState, (ready) => {
            if (ready) {
                void loadPlacePhotos();
            }
        });
        watch(mapInstance, (instance) => {
            if (instance) {
                applyInteractionRestrictions();
                updateMarker();
                void loadPlacePhotos();
            }
        });
        watch(placeLabelText, () => {
            if (markerInstance.value) {
                applyMarkerTitle(placeLabelText.value);
            }
        });
        watch(() => placeMetadata.value, (metadata) => {
            if (metadata) {
                isLabelVisible.value = true;
            }
        }, { deep: true });
        watch(primaryPlacePhoto, (photo) => {
            if (photo) {
                isLabelVisible.value = true;
            }
        });
        watch(mapContainer, () => {
            if (mapContainer.value && mapStatus.configured) {
                ensureMapReady();
            }
        });
        onMounted(async () => {
            await ensureCoordinates();
            await fetchMapStatus();
            // Emit initial place details if already resolved
            if (placeMetadata.value) {
                emit('place-details', { ...placeMetadata.value });
            }
        });
        onBeforeUnmount(() => {
            abortController?.abort();
            clearMarker();
            mapInstance.value = null;
            mapInitPromise = null;
            mapReadyState.value = false;
        });
        __expose({
            placeLabel: placeLabelText,
            mapReady: mapReadyState
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _mergeProps({
                class: ["project-location-map relative w-full overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900", _ctx.heightClass]
            }, _ctx.$attrs), [
                (!normalizedPlaceId.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [...(_cache[0] || (_cache[0] = [
                            _createElementVNode("p", null, "Keine Google-Maps-Referenz vorhanden.", -1 /* CACHED */),
                            _createElementVNode("p", { class: "text-xs opacity-70" }, "Hinterlege eine Place ID im Projekt, um die Karte anzuzeigen.", -1 /* CACHED */)
                        ]))]))
                    : (isFetchingPlace.value || mapStatus.checking)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [...(_cache[1] || (_cache[1] = [
                                _createElementVNode("div", {
                                    class: "map-spinner",
                                    "aria-hidden": "true"
                                }, null, -1 /* CACHED */),
                                _createElementVNode("p", { class: "text-sm font-semibold text-neutral-700 dark:text-neutral-200" }, "Google Maps wird vorbereitet …", -1 /* CACHED */)
                            ]))]))
                        : (mapStatus.configured && !mapError.value)
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 2,
                                ref_key: "mapContainer",
                                ref: mapContainer,
                                class: "absolute inset-0"
                            }, null, 512 /* NEED_PATCH */))
                            : (useIframeFallback.value && iframeUrl.value)
                                ? (_openBlock(), _createElementBlock("iframe", {
                                    key: 3,
                                    class: "absolute inset-0 h-full w-full border-0",
                                    loading: "lazy",
                                    title: _ctx.iframeTitle,
                                    src: iframeUrl.value,
                                    referrerpolicy: "no-referrer-when-downgrade"
                                }, null, 8 /* PROPS */, _hoisted_3))
                                : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                    _createElementVNode("p", null, _toDisplayString(mapError.value || 'Google Maps konnte nicht geladen werden.'), 1 /* TEXT */),
                                    (externalMapUrl.value)
                                        ? (_openBlock(), _createElementBlock("p", _hoisted_5, [
                                            _createElementVNode("a", {
                                                href: externalMapUrl.value,
                                                target: "_blank",
                                                rel: "noreferrer",
                                                class: "map-link"
                                            }, "In Google Maps öffnen", 8 /* PROPS */, _hoisted_6)
                                        ]))
                                        : _createCommentVNode("v-if", true)
                                ])),
                _createVNode(_Transition, { name: "fade" }, {
                    default: _withCtx(() => [
                        (placeTagVisible.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                (primaryPlacePhoto.value)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                        _createElementVNode("img", {
                                            src: primaryPlacePhoto.value.url,
                                            alt: placeLabelText.value
                                        }, null, 8 /* PROPS */, _hoisted_9)
                                    ]))
                                    : _createCommentVNode("v-if", true),
                                _createElementVNode("div", _hoisted_10, [
                                    _cache[2] || (_cache[2] = _createElementVNode("div", { class: "place-tag__row" }, null, -1 /* CACHED */)),
                                    (placeMetadata.value?.formattedAddress)
                                        ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(placeMetadata.value.formattedAddress), 1 /* TEXT */))
                                        : _createCommentVNode("v-if", true)
                                ]),
                                _createElementVNode("button", {
                                    type: "button",
                                    class: "place-tag__close",
                                    onClick: closePlaceTag,
                                    "aria-label": "Tag schließen"
                                }, "×")
                            ]))
                            : _createCommentVNode("v-if", true)
                    ]),
                    _: 1 /* STABLE */
                })
            ], 16 /* FULL_PROPS */));
        };
    }
});
